import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createNotification } from "@/lib/createNotification";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import type { PaginationState } from "./usePagination";

export interface Bill {
  id: string;
  bill_number: string;
  bill_date: string;
  due_date: string | null;
  status: string;
  subtotal: number;
  retainage_amount: number;
  retainage_percent: number;
  total: number;
  description: string | null;
  created_at: string;
  paid_at: string | null;
  payment_reference: string | null;
  void_reason: string | null;
  project_id: string;
  vendor_id: string | null;
  contractor_id: string | null;
  work_order_id: string | null;
  needs_project_assignment?: boolean;
  source?: string | null;
  projects?: { name: string; project_number: string | null } | null;
  vendors?: { name: string } | null;
}

export interface BillFilters {
  search?: string;
  projectFilter?: "all" | "assigned" | "unassigned";
  statusFilter?: string;
  vendorSearch?: string;
  amountMin?: number;
  amountMax?: number;
  dateFrom?: string;
  dateTo?: string;
}

/** Apply shared filter conditions to a Supabase query builder */
function applyBillFilters(query: any, filters?: BillFilters) {
  if (!filters) return query;

  if (filters.projectFilter === "assigned") {
    query = query.not("project_id", "is", null);
  } else if (filters.projectFilter === "unassigned") {
    query = query.is("project_id", null);
  }

  if (filters.statusFilter && filters.statusFilter !== "all") {
    query = query.eq("status", filters.statusFilter);
  }

  if (filters.amountMin != null && !Number.isNaN(filters.amountMin)) {
    query = query.gte("total", filters.amountMin);
  }
  if (filters.amountMax != null && !Number.isNaN(filters.amountMax)) {
    query = query.lte("total", filters.amountMax);
  }
  if (filters.dateFrom) {
    query = query.gte("bill_date", filters.dateFrom);
  }
  if (filters.dateTo) {
    query = query.lte("bill_date", filters.dateTo);
  }

  if (filters.search && filters.search.trim()) {
    const term = `%${filters.search.trim()}%`;
    query = query.or(`bill_number.ilike.${term},description.ilike.${term}`);
  }

  return query;
}

export function useBills(pagination?: PaginationState, filters?: BillFilters) {
  const searchTerm = filters?.search?.trim() ?? "";
  const vendorSearch = filters?.vendorSearch?.trim() ?? "";

  const filterKey = [
    filters?.projectFilter,
    filters?.statusFilter,
    searchTerm,
    vendorSearch,
    filters?.amountMin,
    filters?.amountMax,
    filters?.dateFrom,
    filters?.dateTo,
  ];

  // Resolve vendor IDs matching vendorSearch once and reuse
  const { data: vendorIdsFilter } = useQuery<string[] | null>({
    queryKey: ["bills", "vendor_ids_filter", vendorSearch],
    queryFn: async () => {
      if (!vendorSearch) return null;
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];
      const { data } = await supabase
        .from("vendors")
        .select("id")
        .eq("company_id", companyId)
        .ilike("name", `%${vendorSearch}%`);
      return (data ?? []).map((v: any) => v.id);
    },
    enabled: vendorSearch.length > 0,
  });

  const vendorIdsReady = !vendorSearch || vendorIdsFilter !== undefined;
  const noVendorMatches = vendorSearch && vendorIdsFilter && vendorIdsFilter.length === 0;

  const { data: totalCount } = useQuery({
    queryKey: ["bills", "count", ...filterKey],
    queryFn: async () => {
      if (noVendorMatches) return 0;
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return 0;

      let query = supabase
        .from("bills")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId);

      query = applyBillFilters(query, filters);
      if (vendorSearch && vendorIdsFilter && vendorIdsFilter.length > 0) {
        query = query.in("vendor_id", vendorIdsFilter);
      }

      const { count } = await query;
      return count ?? 0;
    },
    enabled: !!pagination && vendorIdsReady,
  });

  useEffect(() => {
    if (pagination && totalCount != null) {
      pagination.setTotalCount(totalCount);
    }
  }, [totalCount, pagination?.setTotalCount]);

  const billsQuery = useQuery<Bill[]>({
    queryKey: ["bills", pagination?.page, pagination?.pageSize, ...filterKey],
    queryFn: async () => {
      if (noVendorMatches) return [];
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];

      let query = supabase
        .from("bills")
        .select("id, bill_number, bill_date, due_date, status, subtotal, retainage_amount, retainage_percent, total, description, created_at, paid_at, payment_reference, void_reason, project_id, vendor_id, contractor_id, work_order_id, needs_project_assignment, source, projects(name, project_number), vendors(name)")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      query = applyBillFilters(query, filters);
      if (vendorSearch && vendorIdsFilter && vendorIdsFilter.length > 0) {
        query = query.in("vendor_id", vendorIdsFilter);
      }

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Bill[];
    },
    enabled: vendorIdsReady,
  });

  // If there's a search term, we also need to find bills matching vendor name
  // Do a separate query for vendor-name matches and merge
  const vendorSearchQuery = useQuery<Bill[]>({
    queryKey: ["bills", "vendor_search", pagination?.page, pagination?.pageSize, filters?.projectFilter, filters?.statusFilter, searchTerm],
    queryFn: async () => {
      if (!searchTerm) return [];
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];

      // Find vendor IDs matching the search term
      const { data: matchingVendors } = await supabase
        .from("vendors")
        .select("id")
        .eq("company_id", companyId)
        .ilike("name", `%${searchTerm}%`);

      if (!matchingVendors?.length) return [];

      const vendorIds = matchingVendors.map((v: any) => v.id);

      let query = supabase
        .from("bills")
        .select("id, bill_number, bill_date, due_date, status, subtotal, retainage_amount, retainage_percent, total, description, created_at, paid_at, payment_reference, void_reason, project_id, vendor_id, contractor_id, work_order_id, needs_project_assignment, source, projects(name, project_number), vendors(name)")
        .eq("company_id", companyId)
        .in("vendor_id", vendorIds)
        .order("created_at", { ascending: false });

      // Apply non-search filters
      if (filters?.projectFilter === "assigned") {
        query = query.not("project_id", "is", null);
      } else if (filters?.projectFilter === "unassigned") {
        query = query.is("project_id", null);
      }
      if (filters?.statusFilter && filters.statusFilter !== "all") {
        query = query.eq("status", filters.statusFilter);
      }

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Bill[];
    },
    enabled: !!searchTerm,
  });

  // Merge results: bills from main query + vendor search, deduplicated
  const mergedData = useMergedBills(billsQuery.data, vendorSearchQuery.data, searchTerm);

  return {
    ...billsQuery,
    data: mergedData,
  };
}

function useMergedBills(mainBills?: Bill[], vendorBills?: Bill[], searchTerm?: string): Bill[] | undefined {
  if (!mainBills) return undefined;
  if (!searchTerm || !vendorBills?.length) return mainBills;

  const seen = new Set(mainBills.map((b) => b.id));
  const merged = [...mainBills];
  for (const b of vendorBills) {
    if (!seen.has(b.id)) {
      merged.push(b);
      seen.add(b.id);
    }
  }
  return merged;
}

export function useOpenBillsCount() {
  return useQuery<number>({
    queryKey: ["bills", "open_count"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return 0;

      const { count, error } = await supabase
        .from("bills")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .in("status", ["draft", "pending", "approved"]);

      if (error) throw error;
      return count ?? 0;
    },
  });
}

// ── Bill Status Mutations ──────────────────────────────────────────────────

/** Log per-line entries to bill_payment_log (non-blocking) */
async function logBillLines(opts: {
  billId: string;
  action: string;
  userId?: string;
  paymentMethod?: string;
  paymentReference?: string;
  paymentDate?: string;
  amountPaid?: number;
}) {
  try {
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) return;

    const { data: lines } = await supabase
      .from("bill_lines")
      .select("id, description, line_total")
      .eq("bill_id", opts.billId);

    if (!lines || lines.length === 0) return;

    const { data: bill } = await supabase
      .from("bills")
      .select("total")
      .eq("id", opts.billId)
      .single();
    const billTotal = Number(bill?.total ?? 0);

    const rows = lines.map((line: any) => {
      const lineTotal = Number(line.line_total ?? 0);
      const proportion = billTotal > 0 ? lineTotal / billTotal : 0;
      const paymentAmount = opts.amountPaid != null ? Number((opts.amountPaid * proportion).toFixed(2)) : 0;

      return {
        company_id: companyId,
        bill_id: opts.billId,
        bill_line_id: line.id,
        action: opts.action,
        line_description: line.description ?? null,
        line_amount: lineTotal,
        payment_amount: paymentAmount,
        payment_method: opts.paymentMethod ?? null,
        payment_reference: opts.paymentReference ?? null,
        payment_date: opts.paymentDate ?? null,
        recorded_by: opts.userId ?? null,
      };
    });

    await supabase.from("bill_payment_log").insert(rows as any);
  } catch { /* non-blocking */ }
}

/** Approve a draft/pending bill */
export function useApproveBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (billId: string) => {
      const { error } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: billId,
        p_updates: { status: "approved", approved_at: new Date().toISOString() },
      });
      if (error) throw error;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { data: billRow } = await supabase.from("bills").select("project_id").eq("id", billId).single();
      try {
        const { data: approveCompanyId } = await supabase.rpc("my_company_id");
        await supabase.from("ops_audit_log").insert({
          company_id: approveCompanyId,
          table_name: "bills", record_id: billId,
          action: "bill_approved", old_status: "pending", new_status: "approved",
          changed_by: userId,
        } as any);
      } catch { /* non-blocking */ }

      // Per-line approval log
      await logBillLines({ billId, action: "approved", userId });

      createNotification({
        entity_type: "bill", entity_id: billId,
        project_id: billRow?.project_id ?? null,
        type: "bill_approved", title: "Bill Approved",
        message: `A bill has been approved.`,
        priority: "normal", created_by: userId,
        action_url: billRow?.project_id ? `/controller/projects/${billRow.project_id}?tab=work_orders` : `/controller/bills`,
      });
    },
    onSuccess: (_data, billId) => {
      invalidateFinancials(qc, { billId });
    },
  });
}

/** Mark an approved bill as paid */
export function useMarkBillPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      billId, paymentReference, paidAt, paymentMethod, amountPaid,
    }: {
      billId: string;
      paymentReference?: string;
      paidAt?: string;
      paymentMethod?: string;
      amountPaid?: number;
    }) => {
      // Fetch bill total to determine paid vs partially_paid
      const { data: bill } = await supabase.from("bills").select("total, status").eq("id", billId).single();
      const billTotal = Number(bill?.total ?? 0);
      const paid = amountPaid ?? billTotal;
      const finalStatus = paid < billTotal ? "partially_paid" : "paid";

      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { error } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: billId,
        p_updates: {
          status: finalStatus,
          paid_at: paidAt ?? new Date().toISOString(),
          paid_by: userId,
          payment_reference: paymentReference ?? null,
          payment_method: paymentMethod ?? null,
          amount_paid: paid,
        },
      });
      if (error) throw error;
      try {
        const { data: payCompanyId } = await supabase.rpc("my_company_id");
        await supabase.from("ops_audit_log").insert({
          company_id: payCompanyId,
          table_name: "bills", record_id: billId,
          action: finalStatus === "paid" ? "bill_paid" : "bill_partially_paid",
          old_status: bill?.status ?? "approved", new_status: finalStatus,
          changed_by: userId,
          metadata: { payment_reference: paymentReference ?? null, payment_method: paymentMethod ?? null, amount_paid: paid },
        } as any);
      } catch { /* non-blocking */ }

      // Per-line payment log
      await logBillLines({
        billId,
        action: finalStatus,
        userId,
        paymentMethod,
        paymentReference,
        paymentDate: paidAt ?? new Date().toISOString(),
        amountPaid: paid,
      });

      const fmtAmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paid);
      const { data: paidBillRow } = await supabase.from("bills").select("project_id").eq("id", billId).single();
      createNotification({
        entity_type: "bill", entity_id: billId,
        project_id: paidBillRow?.project_id ?? null,
        type: finalStatus === "paid" ? "bill_paid" : "bill_partially_paid",
        title: finalStatus === "paid" ? "Bill Paid" : "Bill Partially Paid",
        message: `Payment of ${fmtAmt} recorded${paymentMethod ? ` via ${paymentMethod}` : ""}.`,
        priority: "normal", created_by: userId,
        action_url: paidBillRow?.project_id ? `/controller/projects/${paidBillRow.project_id}?tab=work_orders` : `/controller/bills`,
      });
    },
    onSuccess: (_data, vars) => {
      invalidateFinancials(qc, { billId: vars.billId });
    },
  });
}

/** Permanently delete a bill and all its lines from the database */
export function useHardDeleteBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (billId: string) => {
      // Use the SECURITY DEFINER RPC so the delete bypasses RLS status=draft restriction
      const { error } = await supabase.rpc("hard_delete_bill" as any, { p_bill_id: billId });
      if (error) throw error;

      // Audit log (non-blocking)
      try {
        const { data: companyId } = await supabase.rpc("my_company_id");
        const userId = (await supabase.auth.getUser()).data.user?.id;
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "bills", record_id: billId,
          action: "bill_hard_deleted",
          changed_by: userId,
        } as any);
      } catch { /* non-blocking */ }
    },
    onSuccess: (_data, billId) => {
      qc.invalidateQueries({ queryKey: ["work_orders"] });
      invalidateFinancials(qc, { billId });
    },
  });
}

/** Void a bill with a required reason */
export function useVoidBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ billId, reason }: { billId: string; reason: string }) => {
      const { data: bill } = await supabase.from("bills").select("status").eq("id", billId).single();
      const { error } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: billId,
        p_updates: { status: "voided", void_reason: reason },
      });
      if (error) throw error;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      try {
        const { data: voidCompanyId } = await supabase.rpc("my_company_id");
        await supabase.from("ops_audit_log").insert({
          company_id: voidCompanyId,
          table_name: "bills", record_id: billId,
          action: "bill_voided", old_status: bill?.status ?? null, new_status: "voided",
          changed_by: userId,
          metadata: { void_reason: reason },
        } as any);
      } catch { /* non-blocking */ }

      const { data: voidBillRow } = await supabase.from("bills").select("project_id").eq("id", billId).single();
      createNotification({
        entity_type: "bill", entity_id: billId,
        project_id: voidBillRow?.project_id ?? null,
        type: "bill_voided", title: "Bill Voided",
        message: `A bill was voided. Reason: ${reason}`,
        priority: "high", created_by: userId,
        action_url: voidBillRow?.project_id ? `/controller/projects/${voidBillRow.project_id}?tab=work_orders` : `/controller/bills`,
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["work_orders"] });
      invalidateFinancials(qc, { billId: vars.billId });
    },
  });
}
